import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Trash2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const downloadTemplate = async () => {
  try {
    const r = await api.get("/admin/quiz-template", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "yallahero-quiz-template.xlsx";
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

const QuestionPreview = ({ q, i }) => (
  <li className="border border-[#E5DCCB] p-3 text-sm" data-testid={`admin-quiz-question-${i}`}>
    <p className="font-semibold" dir="auto">{i + 1}. {q.text}</p>
    {q.type === "tf"
      ? <p className="mt-1 text-[#047857]">الإجابة: {q.answer === 0 ? "صح" : "خطأ"}</p>
      : (
        <ol className="mt-1 space-y-0.5">
          {q.options.map((o, k) => (
            <li key={k} dir="auto" className={k === q.answer ? "text-[#047857] font-semibold" : "text-stone-700"}>
              {k + 1}) {o} {k === q.answer && <CheckCircle2 className="inline w-4 h-4" />}
            </li>
          ))}
        </ol>
      )}
  </li>
);

// Admin: attach the comprehension questions of one book from the Excel template.
export const QuizDialog = ({ book, onClose, onChanged }) => {
  const [quiz, setQuiz] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const id = book?.id;

  useEffect(() => {
    setQuiz(null);
    if (!id) return;
    api.get(`/admin/books/${id}/quiz`).then((r) => setQuiz(r.data)).catch(() => setQuiz({ questions: [] }));
  }, [id]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/books/${id}/quiz`, fd);
      setQuiz({ questions: r.data?.questions ?? [], file_name: file.name });
      toast.success(`تم حفظ ${r.data?.count ?? 0} سؤالاً`);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e), { duration: 9000 }); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/admin/books/${id}/quiz`); setQuiz({ questions: [] }); toast.success("تم حذف الاختبار"); onChanged?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
  return (
    <Dialog open={Boolean(book)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-quiz-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="font-display text-2xl">اختبار فهم المقروء</DialogTitle>
          <DialogDescription dir="auto">{book?.title || book?.original_filename}</DialogDescription>
        </DialogHeader>
        <ol className="text-sm text-stone-700 space-y-1 list-decimal ps-5">
          <li>حمّل النموذج، واكتب الأسئلة في الورقة الأولى (صح/خطأ أو اختيار من متعدد).</li>
          <li>ارفع الملف هنا. يصحّح الموقع إجابات الطالب تلقائياً، ويستطيع الطالب الإجابة مرة واحدة فقط.</li>
        </ol>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadTemplate} data-testid="admin-quiz-template-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-full border-2 border-[#047857] text-[#047857] font-semibold text-sm hover:bg-[#047857] hover:text-white transition-colors">
            <Download className="w-4 h-4" /> تحميل نموذج Excel
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="admin-quiz-upload-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#1C1917] text-white font-semibold text-sm hover:bg-[#8B3A2B] disabled:opacity-60 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} {questions.length ? "استبدال ملف الأسئلة" : "رفع ملف الأسئلة"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="admin-quiz-file-input" />
          {questions.length > 0 && (
            <button onClick={remove} disabled={busy} data-testid="admin-quiz-delete-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-red-800 text-red-800 font-semibold text-sm hover:bg-red-800 hover:text-white transition-colors">
              <Trash2 className="w-4 h-4" /> حذف الاختبار
            </button>
          )}
        </div>
        {quiz === null ? <Loader2 className="w-5 h-5 animate-spin" /> : questions.length ? (
          <div>
            <p className="font-semibold mb-2" data-testid="admin-quiz-count">{questions.length} سؤالاً</p>
            <ul className="space-y-2">{questions.map((q, i) => <QuestionPreview key={i} q={q} i={i} />)}</ul>
          </div>
        ) : <p className="text-sm text-stone-600" data-testid="admin-quiz-empty">لا يوجد اختبار لهذا الكتاب بعد.</p>}
      </DialogContent>
    </Dialog>
  );
};
